import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { getFirestore, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { app } from '../firebase';
import SEO from '../components/SEO';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const db = getFirestore(app);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const q = query(
          collection(db, 'blog_posts'),
          where('status', '==', 'published'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setPosts(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate(),
            publishedAt: d.data().publishedAt?.toDate(),
          }))
        );
      } catch (err) {
        console.error('Error fetching blog posts:', err);
      }
      setLoading(false);
    };
    fetchPosts();
  }, [db]);

  const formatDate = (post) => {
    const d = post.publishedAt || post.createdAt;
    if (!d) return '';
    return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isoDate = (post) => {
    const d = post.publishedAt || post.createdAt;
    return d ? d.toISOString().split('T')[0] : '';
  };

  return (
    <>
      <SEO
        title="Blog"
        description="Insights on medical device innovation, AI technology, regulatory strategy, and the future of digital health from Dr. Julian Shapley."
        path="/blog"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] -top-40 -right-40" />
        <div className="absolute w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] bottom-0 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">
            Insights
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-4">
            Latest from P2P{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
              Digital
            </span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl mx-auto">
            Thoughts and insights on medical device innovation, AI technology, and the future of
            healthcare.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400">No posts yet — check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_rgba(14,165,233,0.12)] group"
              >
                <article>
                  <div
                    className={`h-48 bg-gradient-to-br ${
                      post.gradient || 'from-cyan-500/20 to-indigo-500/20'
                    } relative`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                        <span className="text-2xl font-display font-bold text-white/40">P2P</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-mono font-semibold uppercase tracking-wider">
                      {post.category}
                    </span>
                    <h2 className="font-display text-xl font-bold text-white mt-3 group-hover:text-cyan-300 transition-colors">
                      {post.title}
                    </h2>

                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <time dateTime={isoDate(post)}>{formatDate(post)}</time>
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" /> {post.author}
                      </span>
                      {post.readTime && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {post.readTime}
                        </span>
                      )}
                    </div>

                    <p className="text-slate-400 text-sm mt-4 leading-relaxed">
                      {post.description}
                    </p>

                    <span className="flex items-center gap-2 mt-6 text-cyan-400 text-sm font-semibold group-hover:gap-3 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="relative rounded-3xl overflow-hidden p-16 text-center bg-gradient-to-r from-cyan-600/20 via-indigo-600/20 to-cyan-600/20">
          <div className="absolute w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[100px] -top-20 -right-20" />
          <div className="absolute w-[300px] h-[300px] bg-indigo-500/10 rounded-full blur-[100px] -bottom-20 -left-20" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Stay Updated
            </h2>
            <p className="mt-4 text-slate-300 text-lg max-w-xl mx-auto">
              Follow our latest insights on medical device innovation, AI technology, and healthcare
              transformation.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Blog;
